/**
 * Main Application Component - Helfermanagement Römerman 2026
 * 
 * This is the root component that manages the main application state and renders:
 * - Login screen for authentication
 * - Registration popup for registering new helpers
 * - Helper list popup for viewing and managing all helpers
 * - Fachbereich management popup for viewing helpers by department
 * - User management popup for admins
 * 
 * Features:
 * - User authentication with role-based access
 * - Helper registration via popup
 * - Helper list viewing with filtering
 * - Fachbereich-specific helper management
 * - User management (Admin only)
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './App.css';
import { useAuth } from './contexts/AuthContext';
import { useFachbereiche } from './contexts/FachbereicheContext';
import { useBackendStatus } from './contexts/BackendStatusContext';
import { API_BASE_URL } from './constants';
import HelperList, { Helper } from './components/HelperList';
import FachbereichManagement, { FachbereichHelperList } from './components/FachbereichManagement';
import RegistrationPopup from './components/RegistrationPopup';
import GoogleSheetsSync from './components/GoogleSheetsSync';
import Help from './components/Help';
import Login from './components/Login';
import UserManagement from './components/UserManagement';
import ChangePassword from './components/ChangePassword';
import Statistics from './components/Statistics';
import FachbereicheManagement from './components/FachbereicheManagement';
import YearManagement from './components/YearManagement';
import BackendStatusBanner from './components/BackendStatusBanner';
import SupportEmailModal from './components/SupportEmailModal';
import { convertHelperBereitFuerMehrereEinsaetze } from './utils';
import { Document, Page, PDFDownloadLink, Text, View } from "@react-pdf/renderer";
import LabelsExportPopup, {HelperLabel} from "./components/LabelsExportPopup";

// Helper function to format phone number columns as text in Excel to preserve leading zeros
const formatPhoneColumnAsText = (worksheet: XLSX.WorkSheet, columnName: string): void => {
  if (!worksheet || !worksheet['!ref']) return;
  
  const range = XLSX.utils.decode_range(worksheet['!ref']);
  if (!range || range.e.r < 0) return;
  
  // Find the column index for the phone column
  const headerRow = 0;
  let phoneColumnIndex = -1;
  
  // Get header row to find column index
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellRef = XLSX.utils.encode_cell({ r: headerRow, c: col });
    const cell = worksheet[cellRef];
    if (cell && (cell.v === columnName || cell.w === columnName)) {
      phoneColumnIndex = col;
      break;
    }
  }
  
  if (phoneColumnIndex === -1) return;
  
  // Format all cells in the phone column as text
  for (let row = range.s.r + 1; row <= range.e.r; row++) {
    const cellRef = XLSX.utils.encode_cell({ r: row, c: phoneColumnIndex });
    const cell = worksheet[cellRef];
    if (cell) {
      // Set cell type to string and format as text
      cell.t = 's'; // string type
      cell.z = '@'; // text format code in Excel
      // Ensure value is stored as string
      if (cell.v !== null && cell.v !== undefined) {
        cell.v = String(cell.v);
      }
    }
  }
};

const AppContent: React.FC = () => {
  const { user, loading, logout, isAdmin, isViewer, canRegisterHelpers, canViewAllFachbereiche, canUseGoogleFormOrSync, token, year, changeYear, availableYears, refreshUser } = useAuth();
  const { fachbereiche, refreshFachbereiche } = useFachbereiche();
  const { isBackendAvailable, pauseHealthCheck, resumeHealthCheck } = useBackendStatus();
  const [showHelperList, setShowHelperList] = useState(false);
  const [selectedFachbereich, setSelectedFachbereich] = useState<string | null>(null);
  const [helperToEdit, setHelperToEdit] = useState<Helper | null>(null);
  const [fachbereichHelpers, setFachbereichHelpers] = useState<any[]>([]);
  const [loadingFachbereichHelpers, setLoadingFachbereichHelpers] = useState(false);
  const [showRegistrationPopup, setShowRegistrationPopup] = useState(false);
  const [registrationPopupMode, setRegistrationPopupMode] = useState<'form' | 'excel-import'>('form');
  const [showHelp, setShowHelp] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [showFachbereicheManagement, setShowFachbereicheManagement] = useState(false);
  const [showYearManagement, setShowYearManagement] = useState(false);
  const [showVerwaltungMenu, setShowVerwaltungMenu] = useState(false);
  const [showFachbereichMenu, setShowFachbereichMenu] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showFachbereichManagerMenu, setShowFachbereichManagerMenu] = useState(false);
  const [showFachbereichHelferMenu, setShowFachbereichHelferMenu] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [restoreFile, setRestoreFile] = useState<File | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [overwriteMode, setOverwriteMode] = useState(false);
  const [restoreProgress, setRestoreProgress] = useState<{ step: number; stepName: string; currentArea?: string; totalAreas?: number } | null>(null);
  const restoreFileInputRef = useRef<HTMLInputElement>(null);
  const [showSupportEmailModal, setShowSupportEmailModal] = useState(false);
  const [showGoogleSheetsSync, setShowGoogleSheetsSync] = useState(false);
  const [yearMaintenanceDisabled, setYearMaintenanceDisabled] = useState<boolean>(false);
  const [yearRegistrationConfig, setYearRegistrationConfig] = useState<{ googleFormUrl: string | null; googleSheetsConfigured: boolean }>({ googleFormUrl: null, googleSheetsConfigured: false });
  const [showHelferRegistrierungDropdown, setShowHelferRegistrierungDropdown] = useState(false);
  const [preferHelperListForEdit, setPreferHelperListForEdit] = useState(false);
  const [initialShowKuchenspenderForHelperList, setInitialShowKuchenspenderForHelperList] = useState(false);
  const [newHelperCounts, setNewHelperCounts] = useState<Record<string, number>>({});
  const [newHelperUniqueTotal, setNewHelperUniqueTotal] = useState<number>(0);
  const [gesamtHelperStatus, setGesamtHelperStatus] = useState<{ noFachbereich: number; passtNicht: number }>({ noFachbereich: 0, passtNicht: 0 });
  const [googleSheetsCandidateCount, setGoogleSheetsCandidateCount] = useState<number>(0);
  const [showLabelsExportPopup, setShowLabelsExportPopup] = useState(false);

  // Redirect #database-schema to view-diagrams.html where the full schema (incl. years Google columns) is documented
  useEffect(() => {
    if (window.location.hash === '#database-schema') {
      window.location.replace('/view-diagrams.html#database-schema');
    }
  }, []);

  // Scroll container to top when showing main start screen (fixes mobile positioning)
  useEffect(() => {
    if (user && !loading && containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [user, loading]);

  // Fetch maintenance status for current year
  useEffect(() => {
    if (year && token) {
      fetchMaintenanceStatus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchMaintenanceStatus uses token/year from closure
  }, [year, token]);

  // Close registration dropdown and popup when year becomes read-only
  useEffect(() => {
    if (yearMaintenanceDisabled) {
      setShowHelferRegistrierungDropdown(false);
      setShowRegistrationPopup(false);
    }
  }, [yearMaintenanceDisabled]);

  const fetchMaintenanceStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/years/maintenance`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.success && response.data.years && year) {
        const currentYearData = response.data.years.find((y: { year: number; disabled: boolean; google_form_url?: string | null; google_sheets_configured?: boolean }) => y.year === year);
        if (currentYearData) {
          setYearMaintenanceDisabled(currentYearData.disabled || false);
          setYearRegistrationConfig({
            googleFormUrl: currentYearData.google_form_url || null,
            googleSheetsConfigured: currentYearData.google_sheets_configured || false
          });
        } else {
          setYearRegistrationConfig({ googleFormUrl: null, googleSheetsConfigured: false });
        }
      } else {
        setYearRegistrationConfig({ googleFormUrl: null, googleSheetsConfigured: false });
      }
    } catch (err: any) {
      // 401 is expected when not authenticated or token expired – do not log
      if (err?.response?.status !== 401) {
        console.error('Error fetching maintenance status:', err);
      }
      setYearMaintenanceDisabled(false);
    }
  };

  // Function to open support email modal
  const handleSupportEmail = () => {
    if (!token) {
      alert('Nicht angemeldet');
      return;
    }
    setShowSupportEmailModal(true);
  };

  // Get available Fachbereiche based on user role
  const getAvailableFachbereiche = () => {
    if (isAdmin() || isViewer()) {
      // Administrators and Beobachter see all Fachbereiche
      return fachbereiche;
    } else if (user?.fachbereiche && user.fachbereiche.length > 0) {
      // Fachbereich Managers see only their assigned Fachbereiche
      return fachbereiche.filter(fb => user.fachbereiche!.includes(fb));
    } else {
      // If no Fachbereiche assigned, show empty list
      return [];
    }
  };

  const openLabelsExportPopup = async () => {
    setShowExportMenu(false);
    setShowLabelsExportPopup(true);
  };

  const availableFachbereiche = getAvailableFachbereiche();
  const hasSingleFachbereich = availableFachbereiche.length === 1;
  const singleFachbereich = hasSingleFachbereich ? availableFachbereiche[0] : null;
  const verwaltungMenuRef = useRef<HTMLDivElement>(null);
  const fachbereichMenuRef = useRef<HTMLDivElement>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const fachbereichManagerMenuRef = useRef<HTMLDivElement>(null);
  const fachbereichHelferMenuRef = useRef<HTMLDivElement>(null);
  const helferRegistrierungMenuRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle Escape key to close dialogs and dropdown menus
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Close restore backup dialog
        if (showRestoreDialog && !restoring) {
          setShowRestoreDialog(false);
          setRestoreFile(null);
          if (restoreFileInputRef.current) {
            restoreFileInputRef.current.value = '';
          }
          return;
        }
        // Close dropdown menus
        if (showVerwaltungMenu) {
          setShowVerwaltungMenu(false);
          return;
        }
        if (showFachbereichMenu) {
          setShowFachbereichMenu(false);
          return;
        }
        if (showExportMenu) {
          setShowExportMenu(false);
          return;
        }
        if (showFachbereichManagerMenu) {
          setShowFachbereichManagerMenu(false);
          return;
        }
        if (showUserMenu) {
          setShowUserMenu(false);
          return;
        }
        if (showSettingsMenu) {
          setShowSettingsMenu(false);
          return;
        }
        if (showHelferRegistrierungDropdown) {
          setShowHelferRegistrierungDropdown(false);
          return;
        }
        if (showFachbereichHelferMenu) {
          setShowFachbereichHelferMenu(false);
          return;
        }
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showRestoreDialog, restoring, showVerwaltungMenu, showFachbereichMenu, showExportMenu, showFachbereichManagerMenu, showUserMenu, showSettingsMenu, showHelferRegistrierungDropdown, showFachbereichHelferMenu]);

  // Fetch new-helper counts for Fachbereich managers/admins/beobachter (helpers without Rückmeldung and without position)
  const fetchNewHelperCounts = useCallback(() => {
    if (!token || !year) return;
    const hasFachbereichMenu = isAdmin() || isViewer() || (user?.fachbereiche && user.fachbereiche.length > 0);
    if (!hasFachbereichMenu) return;
    axios.get(`${API_BASE_URL}/api/fachbereich/new-helper-counts`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then((res) => {
        if (res.data.success) {
          const counts = res.data.counts || {};
          setNewHelperCounts(counts);
          const sumFromCounts = (Object.values(counts) as number[]).reduce((a, b) => a + b, 0);
          const uniqueNum = Number(res.data.uniqueTotal);
          // Prefer uniqueTotal (no double-counting). Fallback to sum only when unique is invalid or 0 but we have counts.
          const displayTotal = (Number.isFinite(uniqueNum) && uniqueNum > 0) ? uniqueNum : sumFromCounts;
          setNewHelperUniqueTotal(displayTotal);
        }
      })
      .catch(() => { /* Silently ignore - counts are optional */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- use .length to avoid refetch spam from array reference changes
  }, [token, year, isAdmin, isViewer, user?.fachbereiche?.length]);

  useEffect(() => {
    fetchNewHelperCounts();
  }, [fetchNewHelperCounts]);

  // Refetch new-helper counts when user closes the helper list (they may have processed some)
  const prevShowHelperListRef = useRef(showHelperList);
  useEffect(() => {
    if (prevShowHelperListRef.current && !showHelperList) {
      fetchNewHelperCounts();
    }
    prevShowHelperListRef.current = showHelperList;
  }, [showHelperList, fetchNewHelperCounts]);

  // Fetch Gesamthelferliste status counts for admin (no Fachbereich, Passt nicht)
  const fetchGesamtHelperStatus = useCallback(() => {
    if (!token || !year || !isAdmin()) return;
    axios.get(`${API_BASE_URL}/api/admin/gesamthelferliste-status-counts`, {
      params: { year },
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then((res) => {
        if (res.data.success) {
          const d = res.data;
          setGesamtHelperStatus({
            noFachbereich: d.noFachbereich ?? d.no_fachbereich ?? 0,
            passtNicht: d.passtNicht ?? d.passt_nicht ?? 0
          });
        }
      })
      .catch(() => { /* Silently ignore */ });
  }, [token, year, isAdmin]);

  useEffect(() => {
    fetchGesamtHelperStatus();
  }, [fetchGesamtHelperStatus]);

  // Fetch Google Sheets new candidate count when configured and dropdown opens
  const fetchGoogleSheetsCandidateCount = useCallback(() => {
    if (!token || !year || !yearRegistrationConfig.googleSheetsConfigured) return;
    axios.get(`${API_BASE_URL}/api/google-sheets/candidate-count`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then((res) => {
        if (res.data.success && typeof res.data.newCandidateCount === 'number') {
          setGoogleSheetsCandidateCount(res.data.newCandidateCount);
        }
      })
      .catch(() => { /* Silently ignore */ });
  }, [token, year, yearRegistrationConfig.googleSheetsConfigured]);

  useEffect(() => {
    if (showHelferRegistrierungDropdown && yearRegistrationConfig.googleSheetsConfigured) {
      fetchGoogleSheetsCandidateCount();
    }
  }, [showHelferRegistrierungDropdown, yearRegistrationConfig.googleSheetsConfigured, fetchGoogleSheetsCandidateCount]);

  useEffect(() => {
    if (prevShowHelperListRef.current && !showHelperList) {
      fetchGesamtHelperStatus();
    }
  }, [showHelperList, fetchGesamtHelperStatus]);

  // Refetch gesamtHelperStatus when opening HelperList (admin) so badge/banner are up-to-date
  useEffect(() => {
    if (showHelperList && isAdmin()) {
      fetchGesamtHelperStatus();
    }
  }, [showHelperList, isAdmin, fetchGesamtHelperStatus]);

  // Fetch helpers for Fachbereich when opening HelperList as Fachbereich Manager
  useEffect(() => {
    if (showHelperList && !isAdmin() && selectedFachbereich && token) {
      setLoadingFachbereichHelpers(true);
      axios.get(`${API_BASE_URL}/api/fachbereich/${encodeURIComponent(selectedFachbereich)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
        .then(response => {
          if (response.data.success) {
            setFachbereichHelpers(response.data.helpers || []);
          }
        })
        .catch(err => {
          console.error('Error fetching helpers:', err);
          setFachbereichHelpers([]);
        })
        .finally(() => {
          setLoadingFachbereichHelpers(false);
        });
    } else if (!showHelperList) {
      // Clear helpers when closing
      setFachbereichHelpers([]);
    }
  }, [showHelperList, selectedFachbereich, isAdmin, token]);

  // Close Verwaltung menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (verwaltungMenuRef.current && !verwaltungMenuRef.current.contains(target)) {
        setShowVerwaltungMenu(false);
      }
      if (fachbereichMenuRef.current && !fachbereichMenuRef.current.contains(target)) {
        setShowFachbereichMenu(false);
      }
      if (exportMenuRef.current && !exportMenuRef.current.contains(target)) {
        setShowExportMenu(false);
      }
      if (fachbereichManagerMenuRef.current && !fachbereichManagerMenuRef.current.contains(target)) {
        setShowFachbereichManagerMenu(false);
      }
      if (fachbereichHelferMenuRef.current && !fachbereichHelferMenuRef.current.contains(target)) {
        setShowFachbereichHelferMenu(false);
      }
      if (helferRegistrierungMenuRef.current && !helferRegistrierungMenuRef.current.contains(target)) {
        setShowHelferRegistrierungDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleRegistrationSuccess = (firstName: string, surname: string) => {
    // Registration success is handled within the popup
    // Optionally refresh helper list if it's open
    if (showHelperList) {
      // Could trigger a refresh here if needed
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  // Calculate hours between two time strings (HH:MM format)
  // Helper function to get default race day date (June 15th of the year)
  const getDefaultRaceDayDate = (year: number | null): string => {
    const raceYear = year || new Date().getFullYear();
    return `${raceYear}-06-15`; // Default to June 15th
  };

  const calculateHours = (timeFrom: string, timeTo: string | null): string => {
    if (!timeFrom || !timeTo) {
      return '';
    }
    
    try {
      const [hoursFrom, minutesFrom] = timeFrom.split(':').map(Number);
      const [hoursTo, minutesTo] = timeTo.split(':').map(Number);
      
      const fromMinutes = hoursFrom * 60 + minutesFrom;
      const toMinutes = hoursTo * 60 + minutesTo;
      
      // Handle case where timeTo is on the next day
      let diffMinutes = toMinutes - fromMinutes;
      if (diffMinutes < 0) {
        diffMinutes += 24 * 60; // Add 24 hours
      }
      
      const hours = diffMinutes / 60;
      return hours.toFixed(2).replace('.', ',');
    } catch (error) {
      return '';
    }
  };

  // Fetch helpers + assignment orphans (helpers with assignments but not in current year's users list)
  const fetchHelpersWithAssignmentOrphans = async (): Promise<{ helpers: any[]; helperAssignments: { [key: number]: any[] } }> => {
    const [usersRes, assignmentsRes] = await Promise.all([
      axios.get(`${API_BASE_URL}/api/users`, { headers: { 'Authorization': `Bearer ${token}` } }),
      axios.get(`${API_BASE_URL}/api/helpers/assignments`, { headers: { 'Authorization': `Bearer ${token}` } })
    ]);
    const helpersRaw = usersRes.data.success ? (usersRes.data.users || []) : [];
    const helperAssignments: { [key: number]: any[] } = assignmentsRes.data.success ? (assignmentsRes.data.assignments || {}) : {};
    const userIdsFromUsers = new Set(helpersRaw.map((h: any) => h.id));
    const assignmentHelperIds = Object.keys(helperAssignments).map(Number);
    const missingIds = assignmentHelperIds.filter(id => !userIdsFromUsers.has(id));
    let allHelpersRaw = [...helpersRaw];
    if (missingIds.length > 0) {
      const byIdsRes = await axios.get(`${API_BASE_URL}/api/users/by-ids`, {
        params: { ids: missingIds.join(',') },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (byIdsRes.data.success && byIdsRes.data.users?.length) {
        allHelpersRaw = [...allHelpersRaw, ...byIdsRes.data.users];
      }
    }
    const helpers = allHelpersRaw.map((h: any) => convertHelperBereitFuerMehrereEinsaetze(h));
    return { helpers, helperAssignments };
  };

  const exportHelpersDirect = async () => {
    if (!token) {
      alert('Nicht angemeldet');
      return;
    }

    try {
      const { helpers, helperAssignments } = await fetchHelpersWithAssignmentOrphans();
      
      if (helpers.length === 0) {
        alert('Keine Helfer zum Exportieren vorhanden');
        return;
      }

      // Helper function to calculate hours
      const calculateHours = (timeFrom: string, timeTo: string | null): number => {
        if (!timeFrom || !timeTo) return 0;
        try {
          const [hoursFrom, minutesFrom] = timeFrom.split(':').map(Number);
          const [hoursTo, minutesTo] = timeTo.split(':').map(Number);
          const fromMinutes = hoursFrom * 60 + minutesFrom;
          const toMinutes = hoursTo * 60 + minutesTo;
          let diffMinutes = toMinutes - fromMinutes;
          if (diffMinutes < 0) diffMinutes += 24 * 60;
          return diffMinutes / 60;
        } catch (error) {
          return 0;
        }
      };

      // Prepare Excel data: One row per helper-position assignment
      // Minimal helper info (name, email) + detailed position assignment info
      const excelData: any[] = [];
      
      helpers.forEach((helper: any) => {
        const assignments = helperAssignments[helper.id] || [];
        
        if (assignments.length === 0) {
          // Helper with no position assignments - still include them
          excelData.push({
            'Vorname': helper.first_name,
            'Nachname': helper.surname,
            'E-Mail': helper.email || '',
            'Telefon': helper.phone || '',
            'Wunscheinsatzort': helper.wunscheinsatzort || '',
            'Einsatzzeit': helper.einsatzzeit || '',
            'Bereit für mehrere Einsätze': helper.bereit_fuer_mehrere_einsaetze || '',
            'Anmerkung': helper.anmerkung || '',
            'Aufbauhilfe': helper.aufbauhilfe || '',
            'Kuchenspende': helper.kuchenspende !== null && helper.kuchenspende !== undefined ? helper.kuchenspende : '',
            'T-Shirt': helper.tshirt || '',
            'Fachbereiche': helper.fachbereiche && helper.fachbereiche.length > 0 ? helper.fachbereiche.join(', ') : '',
            'Fachbereich': '',
            'Einsatzort': '',
            'Einsatzdatum': '',
            'Einsatzuhrzeit (Von)': '',
            'Einsatzuhrzeit (Bis)': '',
            'Einsatzinformationen': '',
            'Einsatzstunden': ''
          });
        } else {
          // One row per position assignment
          assignments.forEach((hp: any) => {
            const hours = hp.einsatzuhrzeit && hp.einsatzuhrzeit_bis 
              ? calculateHours(hp.einsatzuhrzeit, hp.einsatzuhrzeit_bis).toFixed(2).replace('.', ',')
              : '';
            
            excelData.push({
              'Vorname': helper.first_name,
              'Nachname': helper.surname,
              'E-Mail': helper.email || '',
              'Telefon': helper.phone || '',
              'Wunscheinsatzort': helper.wunscheinsatzort || '',
              'Einsatzzeit': helper.einsatzzeit || '',
              'Bereit für mehrere Einsätze': helper.bereit_fuer_mehrere_einsaetze || '',
              'Anmerkung': helper.anmerkung || '',
              'Aufbauhilfe': helper.aufbauhilfe || '',
              'Kuchenspende': helper.kuchenspende !== null && helper.kuchenspende !== undefined ? helper.kuchenspende : '',
              'T-Shirt': helper.tshirt || '',
              'Fachbereiche': helper.fachbereiche && helper.fachbereiche.length > 0 ? helper.fachbereiche.join(', ') : '',
              'Fachbereich': hp.fachbereich || '',
              'Einsatzort': hp.einsatzort || '',
              'Einsatzdatum': hp.einsatzdatum || getDefaultRaceDayDate(year),
              'Einsatzuhrzeit (Von)': hp.einsatzuhrzeit || '',
              'Einsatzuhrzeit (Bis)': hp.einsatzuhrzeit_bis || '',
              'Einsatzinformationen': hp.einsatzinformationen || '',
              'Einsatzstunden': hours
            });
          });
        }
      });

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      
      // Format phone number column as text to preserve leading zeros
      formatPhoneColumnAsText(worksheet, 'Telefon');
      
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Helfer-Positionen');

      // Set column widths
      const columnWidths = [
        { wch: 15 },  // Vorname
        { wch: 15 },  // Nachname
        { wch: 25 },  // E-Mail
        { wch: 15 },  // Telefon
        { wch: 20 },  // Wunscheinsatzort
        { wch: 15 },  // Einsatzzeit
        { wch: 25 },  // Bereit für mehrere Einsätze
        { wch: 30 },  // Anmerkung
        { wch: 30 },  // Aufbauhilfe
        { wch: 12 },  // Kuchenspende
        { wch: 10 },  // T-Shirt
        { wch: 20 },  // Fachbereiche
        { wch: 20 },  // Fachbereich
        { wch: 30 },  // Einsatzort
        { wch: 12 },  // Einsatzdatum
        { wch: 15 },  // Einsatzuhrzeit (Von)
        { wch: 15 },  // Einsatzuhrzeit (Bis)
        { wch: 50 },  // Einsatzinformationen
        { wch: 12 }   // Einsatzstunden
      ];
      worksheet['!cols'] = columnWidths;

      // Generate filename with current date
      const today = new Date();
      const dateStr = today.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\//g, '-');
      const filename = `Roemerman_${year || 2026}_Helfer_Positionen_${dateStr}.xlsx`;

      // Download file
      XLSX.writeFile(workbook, filename);
    } catch (error: any) {
      console.error('Error exporting helpers and positions:', error);
      alert('Fehler beim Exportieren der Helfer und Positionen: ' + (error.response?.data?.message || error.message || 'Unbekannter Fehler'));
    }
  };

  const exportGesamthelferliste = async () => {
    if (!token) {
      alert('Nicht angemeldet');
      return;
    }

    try {
      const { helpers, helperAssignments } = await fetchHelpersWithAssignmentOrphans();
      
      if (helpers.length === 0) {
        alert('Keine Helfer zum Exportieren vorhanden');
        return;
      }

      // Calculate minutes between two time strings (HH:MM format)
      const calculateMinutes = (timeFrom: string, timeTo: string | null): number => {
        if (!timeFrom || !timeTo) return 0;
        try {
          const [hoursFrom, minutesFrom] = timeFrom.split(':').map(Number);
          const [hoursTo, minutesTo] = timeTo.split(':').map(Number);
          const fromMinutes = hoursFrom * 60 + minutesFrom;
          const toMinutes = hoursTo * 60 + minutesTo;
          let diffMinutes = toMinutes - fromMinutes;
          if (diffMinutes < 0) diffMinutes += 24 * 60;
          return diffMinutes;
        } catch (e) {
          return 0;
        }
      };

      // Format total minutes as "hh:mm" (e.g. 90 -> "01:30", 60 min = 1 hour)
      const formatMinutesAsHHMM = (totalMinutes: number): string => {
        if (totalMinutes <= 0) return '';
        const h = Math.floor(totalMinutes / 60);
        const m = totalMinutes % 60;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      };

      // Build positions+hours column: "<fachbereich>": "<position>" ("hh:mm") | "<position2>" ("hh:mm") ; "<fachbereich2>": ...
      const buildPositionsHoursColumn = (assignments: any[]): string => {
        if (!assignments || assignments.length === 0) return '';
        const byFachbereich = new Map<string, any[]>();
        for (const a of assignments) {
          const fb = a.fachbereich || '';
          if (!byFachbereich.has(fb)) byFachbereich.set(fb, []);
          byFachbereich.get(fb)!.push(a);
        }
        const parts: string[] = [];
        Array.from(byFachbereich.entries()).forEach(([fachbereich, positions]) => {
          const positionParts = positions.map((p: any) => {
            const mins = calculateMinutes(p.einsatzuhrzeit, p.einsatzuhrzeit_bis || null);
            const hhmm = formatMinutesAsHHMM(mins);
            return `${p.einsatzort || ''} (${hhmm})`;
          }).join(' | ');
          parts.push(`${fachbereich}: ${positionParts}`);
        });
        return parts.join(' ; ');
      };

      // Prepare Excel data with position assignments for offline Excel maintenance
      const excelData = helpers.map((helper: any, index: number) => {
        const date = new Date(helper.created_at);
        const assignments = helperAssignments[helper.id] || [];
        
        // Format feedback for Excel - only include if there's actual feedback
        let feedbackText = '';
        if (helper.feedback && helper.feedback.length > 0) {
          const feedbackEntries = helper.feedback.map((fb: any) => {
            let fbText = '';
            if (fb.rückmeldung || fb.bemerkung) {
              fbText = fb.fachbereich + ':';
              if (fb.rückmeldung) fbText += ` Rückmeldung: ${fb.rückmeldung}`;
              if (fb.bemerkung) fbText += ` Bemerkung: ${fb.bemerkung}`;
            }
            return fbText;
          }).filter((text: string) => text.length > 0); // Only include entries with actual feedback
          
          if (feedbackEntries.length > 0) {
            feedbackText = feedbackEntries.join('; ');
          }
        }

        // Sum all minutes from assigned positions (60 min = 1 hour)
        let totalMinutes = 0;
        assignments.forEach((hp: any) => {
          totalMinutes += calculateMinutes(hp.einsatzuhrzeit, hp.einsatzuhrzeit_bis || null);
        });
        const einsatzstundenSumme = formatMinutesAsHHMM(totalMinutes);
        const positionenMitStunden = buildPositionsHoursColumn(assignments);

        return {
          'Nr.': index + 1,
          'Registriert am': date.toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          }),
          'Registriert um': date.toLocaleTimeString('de-DE', {
            hour: '2-digit',
            minute: '2-digit'
          }),
          'Vorname': helper.first_name,
          'Nachname': helper.surname,
          'E-Mail': helper.email || '',
          'Telefon': helper.phone || '',
          'Wunscheinsatzort': helper.wunscheinsatzort || '',
          'Einsatzzeit': helper.einsatzzeit || '',
          'Bereit für mehrere Einsätze': helper.bereit_fuer_mehrere_einsaetze || '',
          'Anmerkung': helper.anmerkung || '',
          'T-Shirt': helper.tshirt || '',
          'Kuchenspende': helper.kuchenspende !== null && helper.kuchenspende !== undefined ? helper.kuchenspende : '',
          'Zuordnung zu Fachbereich': helper.fachbereiche && helper.fachbereiche.length > 0 ? helper.fachbereiche.join(', ') : '',
          'Rückmeldung & Bemerkung': feedbackText,
          'Positionen mit Stunden': positionenMitStunden,
          'Einsatzstunden gesamt (hh:mm)': einsatzstundenSumme
        };
      });

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      
      // Format phone number column as text to preserve leading zeros
      formatPhoneColumnAsText(worksheet, 'Telefon');
      
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Helfer');

      // Set column widths
      const columnWidths = [
        { wch: 5 },   // Nr.
        { wch: 12 },  // Registriert am
        { wch: 12 },  // Registriert um
        { wch: 15 },  // Vorname
        { wch: 15 },  // Nachname
        { wch: 25 },  // E-Mail
        { wch: 15 },  // Telefon
        { wch: 20 },  // Wunscheinsatzort
        { wch: 15 },  // Einsatzzeit
        { wch: 25 },  // Bereit für mehrere Einsätze
        { wch: 30 },  // Anmerkung
        { wch: 10 },  // T-Shirt
        { wch: 12 },  // Kuchenspende
        { wch: 20 },  // Zuordnung zu Fachbereich
        { wch: 40 },  // Rückmeldung & Bemerkung
        { wch: 80 },  // Positionen mit Stunden
        { wch: 12 }   // Einsatzstunden gesamt (hh:mm)
      ];
      worksheet['!cols'] = columnWidths;

      // Generate filename with current date
      const today = new Date();
      const dateStr = today.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\//g, '-');
      const filename = `Roemerman_${year || 2026}_Gesamthelferliste_${dateStr}.xlsx`;

      // Download file
      XLSX.writeFile(workbook, filename);
    } catch (error: any) {
      console.error('Error exporting Gesamthelferliste:', error);
      alert('Fehler beim Exportieren der Gesamthelferliste: ' + (error.response?.data?.message || error.message || 'Unbekannter Fehler'));
    }
  };

  const exportStatistics = async () => {
    if (!token) {
      alert('Nicht angemeldet');
      return;
    }

    try {
      // Fetch statistics
      const response = await axios.get(`${API_BASE_URL}/api/admin/statistics`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.data.success || !response.data.statistics) {
        alert('Fehler beim Laden der Statistiken');
        return;
      }

      const statistics = response.data.statistics || [];
      const totalKuchenspenden = response.data.totalKuchenspenden || 0;
      
      if (statistics.length === 0) {
        alert('Keine Statistiken zum Exportieren vorhanden');
        return;
      }

      // Calculate totals
      const totalDemanded = statistics.reduce((sum: number, stat: any) => sum + stat.demandedHelpers, 0);
      const totalAssignedToFachbereich = statistics.reduce((sum: number, stat: any) => sum + stat.assignedToFachbereich, 0);
      const totalAssignedToPositions = statistics.reduce((sum: number, stat: any) => sum + stat.assignedToPositions, 0);
      const totalRegisteredHelpers = response.data.totalRegisteredHelpers || 0;

      // Helper function to get status text
      // Compare total assignments (helper-position pairs) with total required assignments
      // This accounts for helpers being assigned to multiple positions
      const getStatusText = (demanded: number, totalAssignments: number) => {
        if (demanded === 0) return 'Kein Bedarf';
        if (totalAssignments >= demanded) return 'Ausreichend';
        if (totalAssignments >= demanded * 0.8) return 'Fast ausreichend';
        return 'Unzureichend';
      };

      // Prepare Excel data
      const excelData: any[] = [];

      // Add summary row
      excelData.push({
        'Fachbereich': 'GESAMT',
        'Registrierte Helfer': totalRegisteredHelpers,
        'Bedarf (Positionen)': totalDemanded,
        'Zuordnungen Fachbereiche': totalAssignedToFachbereich,
        'Zuordnungen Positionen': totalAssignedToPositions,
        'Status': '',
        'Abdeckung (%)': totalDemanded > 0 ? Math.round((totalAssignedToPositions / totalDemanded) * 100) : 0,
        'Gesamt Kuchenspenden': totalKuchenspenden
      });

      // Add empty row for separation
      excelData.push({});

      // Add data rows
      statistics.forEach((stat: any) => {
        // Use totalAssignments if available, otherwise fall back to assignedToPositions for backward compatibility
        const totalAssignments = stat.totalAssignments !== undefined ? stat.totalAssignments : stat.assignedToPositions;
        const statusText = getStatusText(stat.demandedHelpers, totalAssignments);
        const percentage = stat.demandedHelpers > 0 
          ? Math.round((totalAssignments / stat.demandedHelpers) * 100) 
          : 0;

        excelData.push({
          'Fachbereich': stat.fachbereich,
          'Bedarf (Positionen)': stat.demandedHelpers,
          'Zuordnungen Fachbereiche': stat.assignedToFachbereich,
          'Zuordnungen Positionen': stat.assignedToPositions,
          'Status': statusText,
          'Abdeckung (%)': percentage
        });
      });

      // Create Excel file
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Statistiken');

      // Set column widths
      const columnWidths = [
        { wch: 25 },  // Fachbereich
        { wch: 20 },  // Registrierte Helfer
        { wch: 18 },  // Bedarf (Positionen)
        { wch: 25 },  // Zuordnungen Fachbereiche
        { wch: 22 },  // Zuordnungen Positionen
        { wch: 18 },  // Status
        { wch: 15 },  // Abdeckung (%)
        { wch: 20 }   // Gesamt Kuchenspenden
      ];
      worksheet['!cols'] = columnWidths;

      const today = new Date();
      const dateStr = today.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\//g, '-');
      const filename = `Roemerman_${year || 2026}_Statistiken_${dateStr}.xlsx`;

      XLSX.writeFile(workbook, filename);
    } catch (error: any) {
      console.error('Error exporting statistics:', error);
      alert('Fehler beim Exportieren der Statistiken: ' + (error.message || 'Unbekannter Fehler'));
    }
  };

  const exportBackup = async () => {
    if (!token) {
      alert('Nicht angemeldet');
      return;
    }
    
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/backup`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      // Create timestamp for fallback filename (format: YYYY-MM-DDTHH-MM-SS)
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      let filename = `Roemerman_${year || 2026}_Backup_${timestamp}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setShowExportMenu(false);
    } catch (error: any) {
      console.error('Error exporting backup:', error);
      alert('Fehler beim Erstellen des Backups: ' + (error.response?.data?.message || error.message || 'Unbekannter Fehler'));
    }
  };

  const handleRestoreFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Prevent file selection if year is read-only
    if (yearMaintenanceDisabled) {
      alert(`Das Jahr ${year} ist schreibgeschützt. Backup kann nicht wiederhergestellt werden.`);
      if (restoreFileInputRef.current) {
        restoreFileInputRef.current.value = '';
      }
      return;
    }

    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        alert('Bitte wählen Sie eine Excel-Datei (.xlsx oder .xls) aus');
        if (restoreFileInputRef.current) {
          restoreFileInputRef.current.value = '';
        }
        return;
      }
      setRestoreFile(file);
    }
  };

  const handleRestore = async () => {
    if (!restoreFile || !token) {
      return;
    }

    // Prevent restore if year is read-only
    if (yearMaintenanceDisabled && year) {
      const errorMsg = `Die Bearbeitung von Daten für Jahr ${year} ist deaktiviert. Backup kann nicht wiederhergestellt werden. Bitte aktivieren Sie die Bearbeitung für dieses Jahr in der Jahresverwaltung.`;
      alert(errorMsg);
      setShowRestoreDialog(false);
      setRestoreFile(null);
      setOverwriteMode(false);
      if (restoreFileInputRef.current) {
        restoreFileInputRef.current.value = '';
      }
      console.error('Restore blocked - year maintenance disabled:', { year });
      return;
    }

    // Confirm restore with appropriate warning
    const warningMessage = overwriteMode
      ? 'WARNUNG: Überschreiben-Modus aktiviert!\n\n' +
        'ALLE bestehenden Daten werden gelöscht und durch die Backup-Daten ersetzt.\n\n' +
        'Dies ist eine DESTRUKTIVE Operation - alle aktuellen Daten gehen verloren!\n\n' +
        'Möchten Sie wirklich fortfahren?'
      : 'WARNUNG: Beim Wiederherstellen werden bestehende Daten aktualisiert oder neue Daten hinzugefügt.\n\n' +
        'Bestehende Datensätze werden anhand ihrer eindeutigen Identifikation (z.B. E-Mail für Helfer) aktualisiert.\n\n' +
        'Möchten Sie fortfahren?';

    const confirmed = window.confirm(warningMessage);

    if (!confirmed) {
      return;
    }

    const restoreSteps = [
      { step: 1, name: 'Stelle Fachbereiche wieder her...', area: 'Fachbereiche' },
      { step: 2, name: 'Stelle Helfer wieder her...', area: 'Helfer' },
      { step: 3, name: 'Stelle System-Benutzer wieder her...', area: 'System-Benutzer' },
      { step: 4, name: 'Stelle Zuordnungen wieder her...', area: 'Zuordnungen' },
      { step: 5, name: 'Stelle Helferpunkte wieder her...', area: 'Helferpunkte' },
      { step: 6, name: 'Stelle Helfer-Helferpunkte wieder her...', area: 'Helfer-Helferpunkte' },
      { step: 7, name: 'Abschließen...', area: 'Abschluss' }
    ];

    const totalSteps = restoreSteps.length;
    let progressInterval: NodeJS.Timeout | null = null;
    let timeoutId: NodeJS.Timeout | null = null;
    let safetyTimeout: NodeJS.Timeout | null = null;
    const progressRef = { currentStep: 0, isComplete: false };

    const cleanup = () => {
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (safetyTimeout) {
        clearTimeout(safetyTimeout);
        safetyTimeout = null;
      }
    };

    const finishRestore = async (success: boolean) => {
      progressRef.isComplete = true;
      cleanup();
      
      // Show completion message
      setRestoreProgress({ step: totalSteps, stepName: success ? 'Abgeschlossen!' : 'Fehler aufgetreten', currentArea: 'Abgeschlossen', totalAreas: totalSteps });
      
      // Clear progress and restore state after delay to ensure user sees completion
      timeoutId = setTimeout(() => {
        setRestoreProgress(null);
        setRestoring(false);
        resumeHealthCheck(); // Resume health checks after restore completes
        timeoutId = null;
      }, 2000); // Increased to 2 seconds so user can see completion message
    };

    setRestoring(true);
    pauseHealthCheck(); // Pause health checks during restore to prevent false "backend unavailable" warnings
    
    // Set initial progress state immediately
    setRestoreProgress({ step: 0, stepName: 'Starte Wiederherstellung...', currentArea: 'Initialisierung', totalAreas: totalSteps });

    // Safety timeout - ensure dialog closes after 5 minutes max
    safetyTimeout = setTimeout(() => {
      console.warn('Restore taking too long, forcing cleanup');
      finishRestore(false);
    }, 300000); // 5 minutes safety timeout

    // Simulate progress updates - advance through steps
    // Start after a small delay to ensure dialog is rendered
    // Don't advance to final step until backend responds
    progressInterval = setInterval(() => {
      if (progressRef.isComplete) {
        cleanup();
        return;
      }
      
      // Don't advance past second-to-last step - wait for backend to finish
      const maxStep = restoreSteps.length - 1;
      if (progressRef.currentStep < maxStep) {
        progressRef.currentStep++;
        const step = restoreSteps[progressRef.currentStep - 1];
        setRestoreProgress({ step: step.step, stepName: step.name, currentArea: step.area, totalAreas: totalSteps });
      } else if (progressRef.currentStep === maxStep) {
        // Stay at second-to-last step, show "Warte auf Abschluss..."
        progressRef.currentStep++;
        const step = restoreSteps[progressRef.currentStep - 1];
        setRestoreProgress({ step: step.step, stepName: 'Warte auf Abschluss...', currentArea: step.area, totalAreas: totalSteps });
      }
      // Don't advance to final step - that will be set when backend responds
    }, 1000); // Update every 1 second (slower to match actual restore time better)

    // Small delay to ensure progress dialog is rendered before starting restore
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      const formData = new FormData();
      formData.append('file', restoreFile);
      formData.append('overwrite', overwriteMode ? 'true' : 'false');

      const response = await axios.post(`${API_BASE_URL}/api/admin/restore`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 300000 // 5 minute timeout
      });

      if (response.data.success) {
        const summary = response.data.summary;
        const errorLog = response.data.errorLog || {};
        let message = response.data.message + '\n\nDetails:\n';
        
        // Display warnings if any
        if (summary.warnings && summary.warnings.length > 0) {
          message += '\n⚠️ WARNUNGEN:\n';
          summary.warnings.forEach((warning: string) => {
            message += `  • ${warning}\n`;
          });
          message += '\n';
        }
        
        Object.entries(summary).forEach(([key, value]: [string, any]) => {
          // Skip warnings object in the details loop
          if (key === 'warnings') {
            return;
          }
          
          const label = {
            fachbereiche: 'Fachbereiche',
            helfer: 'Helfer',
            appUsers: 'System-Benutzer',
            helperFachbereiche: 'Helfer-Fachbereiche',
            userFachbereiche: 'Benutzer-Fachbereiche',
            helferpunkte: 'Helferpunkte',
            helperHelferpunkte: 'Helfer-Helferpunkte'
          }[key] || key;
          
          if (response.data.mode === 'overwrite') {
            message += `${label}: ${value.created || 0} erstellt, ${value.errors || 0} Fehler\n`;
          } else {
            message += `${label}: ${value.created || 0} erstellt, ${value.updated || 0} aktualisiert, ${value.errors || 0} Fehler\n`;
          }
        });

        // Check if there are any errors (exclude warnings from calculation)
        const totalErrors = Object.entries(summary).reduce((sum: number, [key, cat]: [string, any]) => {
          if (key === 'warnings') return sum;
          return sum + (cat.errors || 0);
        }, 0);
        if (totalErrors > 0) {
          // Create detailed error log
          let errorDetails = '\n\n=== DETAILLIERTES FEHLERPROTOKOLL ===\n\n';
          Object.entries(errorLog).forEach(([category, errors]: [string, any]) => {
            if (errors && errors.length > 0) {
              const categoryLabel = {
                fachbereiche: 'Fachbereiche',
                helfer: 'Helfer',
                appUsers: 'System-Benutzer',
                helperFachbereiche: 'Helfer-Fachbereiche',
                userFachbereiche: 'Benutzer-Fachbereiche',
                helferpunkte: 'Helferpunkte',
                helperHelferpunkte: 'Helfer-Helferpunkte'
              }[category] || category;

              errorDetails += `\n--- ${categoryLabel} (${errors.length} Fehler) ---\n`;
              errors.forEach((error: any, index: number) => {
                errorDetails += `\nFehler ${index + 1}:\n`;
                errorDetails += `  Zeitpunkt: ${error.timestamp}\n`;
                errorDetails += `  Operation: ${error.operation}\n`;
                errorDetails += `  Datensatz: ${JSON.stringify(error.record, null, 2).substring(0, 200)}...\n`;
                errorDetails += `  Fehlermeldung: ${error.error}\n`;
                if (error.stack) {
                  errorDetails += `  Stack: ${error.stack.substring(0, 300)}...\n`;
                }
              });
            }
          });

          // Create downloadable error log file
          const errorLogBlob = new Blob([errorDetails], { type: 'text/plain;charset=utf-8' });
          const errorLogUrl = URL.createObjectURL(errorLogBlob);
          const errorLogLink = document.createElement('a');
          errorLogLink.href = errorLogUrl;
          errorLogLink.download = `backup-restore-errors-${new Date().toISOString().replace(/[:.]/g, '-')}.txt`;
          errorLogLink.style.display = 'none';
          document.body.appendChild(errorLogLink);
          errorLogLink.click();
          document.body.removeChild(errorLogLink);
          URL.revokeObjectURL(errorLogUrl);

          message += errorDetails;
          message += '\n\nEin detailliertes Fehlerprotokoll wurde heruntergeladen.';
        }

        if (safetyTimeout) {
          clearTimeout(safetyTimeout);
          safetyTimeout = null;
        }
        // Show final step before completing
        setRestoreProgress({ step: totalSteps, stepName: 'Abschließen...', currentArea: 'Abschluss', totalAreas: totalSteps });
        // Small delay to show final step
        await new Promise(resolve => setTimeout(resolve, 500));
        await finishRestore(true);

        alert(message);
        
        // Refresh data
        refreshFachbereiche();
        setShowRestoreDialog(false);
        setRestoreFile(null);
        setOverwriteMode(false);
        if (restoreFileInputRef.current) {
          restoreFileInputRef.current.value = '';
        }
      } else {
        if (safetyTimeout) {
          clearTimeout(safetyTimeout);
          safetyTimeout = null;
        }
        // Show final step before completing (even on error)
        setRestoreProgress({ step: totalSteps, stepName: 'Fehler aufgetreten...', currentArea: 'Fehler', totalAreas: totalSteps });
        await new Promise(resolve => setTimeout(resolve, 500));
        await finishRestore(false);
        alert('Fehler beim Wiederherstellen: ' + (response.data.message || 'Unbekannter Fehler'));
      }
    } catch (error: any) {
      console.error('Error restoring backup:', error);
      if (safetyTimeout) {
        clearTimeout(safetyTimeout);
        safetyTimeout = null;
      }
      await finishRestore(false);
      
      if (error.code === 'ECONNABORTED' || (error.message && error.message.includes('timeout'))) {
        alert('Die Wiederherstellung dauert länger als erwartet. Bitte prüfen Sie die Server-Logs oder versuchen Sie es später erneut.');
      } else {
        alert('Fehler beim Wiederherstellen des Backups: ' + (error.response?.data?.message || error.message || 'Unbekannter Fehler'));
      }
    } finally {
      // Only set restoring to false if finishRestore hasn't handled it yet
      // finishRestore will handle it after showing completion message (with 2 second delay)
      if (!progressRef.isComplete) {
        setRestoring(false);
        resumeHealthCheck();
      }
    }
  };

  const exportAllHelferpunkte = async () => {
    if (!token) {
      alert('Nicht angemeldet');
      return;
    }
    
    try {
      // Fetch all Helferpunkte from all Fachbereiche
      const allHelferpunkte: any[] = [];
      const helferpunktHelpersMap: { [key: number]: any[] } = {};

      // Fetch Helferpunkte for each Fachbereich
      for (const fachbereich of fachbereiche) {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/api/fachbereich/${encodeURIComponent(fachbereich)}/helferpunkte`,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );

          if (response.data.success && response.data.helferpunkte) {
            allHelferpunkte.push(...response.data.helferpunkte);
          }
        } catch (error) {
          // Ignore errors for individual Fachbereiche
          console.warn(`Error fetching Helferpunkte for ${fachbereich}:`, error);
        }
      }

      if (allHelferpunkte.length === 0) {
        alert('Keine Helferpunkte gefunden');
        return;
      }

      // Fetch helpers for each Helferpunkt
      for (const hp of allHelferpunkte) {
        try {
          const helpersResponse = await axios.get(
            `${API_BASE_URL}/api/helferpunkte/${hp.id}/helpers`,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );

          if (helpersResponse.data.success && helpersResponse.data.helpers) {
            helferpunktHelpersMap[hp.id] = helpersResponse.data.helpers;
          } else {
            helferpunktHelpersMap[hp.id] = [];
          }
        } catch (error) {
          helferpunktHelpersMap[hp.id] = [];
        }
      }

      // Prepare Excel data
      const excelData: any[] = [];
      
      allHelferpunkte.forEach((hp) => {
        const helpers = helferpunktHelpersMap[hp.id] || [];
        const hoursPerEinsatz = calculateHours(hp.einsatzuhrzeit, hp.einsatzuhrzeit_bis);
        
        if (helpers.length === 0) {
          excelData.push({
            'Fachbereich': hp.fachbereich,
            'Einsatzort': hp.einsatzort,
            'Einsatzdatum': hp.einsatzdatum || getDefaultRaceDayDate(year),
            'Einsatzuhrzeit (Von)': hp.einsatzuhrzeit,
            'Einsatzuhrzeit (Bis)': hp.einsatzuhrzeit_bis || '',
            'Anzahl benötigter Helfer': hp.required_assignees || '',
            'Einsatzinformationen': hp.einsatzinformationen || '',
            'Stunden pro Einsatz': hoursPerEinsatz,
            'Vorname': '',
            'Nachname': '',
            'E-Mail': '',
            'Telefon': ''
          });
        } else {
          helpers.forEach((helper: any) => {
            excelData.push({
              'Fachbereich': hp.fachbereich,
              'Einsatzort': hp.einsatzort,
              'Einsatzdatum': hp.einsatzdatum || getDefaultRaceDayDate(year),
              'Einsatzuhrzeit (Von)': hp.einsatzuhrzeit,
              'Einsatzuhrzeit (Bis)': hp.einsatzuhrzeit_bis || '',
              'Anzahl benötigter Helfer': hp.required_assignees || '',
              'Einsatzinformationen': hp.einsatzinformationen || '',
              'Stunden pro Einsatz': hoursPerEinsatz,
              'Vorname': helper.first_name,
              'Nachname': helper.surname,
              'E-Mail': helper.email || '',
              'Telefon': helper.phone || ''
            });
          });
        }
      });

      // Create Excel file
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      
      // Format phone number column as text to preserve leading zeros
      formatPhoneColumnAsText(worksheet, 'Telefon');
      
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Alle Helferpunkte');

      const columnWidths = [
        { wch: 20 },  // Fachbereich
        { wch: 30 },  // Einsatzort
        { wch: 12 },  // Einsatzdatum
        { wch: 15 },  // Einsatzuhrzeit (Von)
        { wch: 15 },  // Einsatzuhrzeit (Bis)
        { wch: 25 },  // Anzahl benötigter Helfer
        { wch: 50 },  // Einsatzinformationen
        { wch: 18 },  // Stunden pro Einsatz
        { wch: 15 },  // Vorname
        { wch: 15 },  // Nachname
        { wch: 25 },  // E-Mail
        { wch: 15 }   // Telefon
      ];
      worksheet['!cols'] = columnWidths;

      const today = new Date();
      const dateStr = today.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\//g, '-');
      const filename = `Roemerman_${year || 2026}_Alle_Helferpunkte_${dateStr}.xlsx`;

      XLSX.writeFile(workbook, filename);
    } catch (error: any) {
      console.error('Error exporting Helferpunkte:', error);
      alert('Fehler beim Exportieren der Helferpunkte: ' + (error.message || 'Unbekannter Fehler'));
    }
  };

  // Close settings menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.settings-menu-container')) {
        setShowSettingsMenu(false);
      }
    };
    if (showSettingsMenu) {
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showSettingsMenu]);

  // Check if password reset is required after login (must be before conditional returns)
  useEffect(() => {
    if (user && user.passwordResetRequired && !showChangePassword) {
      setShowChangePassword(true);
    }
  }, [user, showChangePassword]);

  // Show login screen if not authenticated
  if (loading) {
    return (
      <div className={`App${!isBackendAvailable ? ' backend-banner-visible' : ''}`}>
        <BackendStatusBanner isVisible={!isBackendAvailable} />
        <div className="container">Lädt...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <BackendStatusBanner isVisible={!isBackendAvailable} />
        <Login />
      </>
    );
  }

  return (
    <div className={`App${!isBackendAvailable ? ' backend-banner-visible' : ''}`}>
      <BackendStatusBanner isVisible={!isBackendAvailable} />
      <div className="container" ref={containerRef}>
        <div className="container-header-buttons">
        <button 
          className="help-button"
          onClick={() => setShowHelp(true)}
          title="Hilfe & Anleitung anzeigen"
        >
          ?
        </button>
          <button 
            className="help-button"
            onClick={handleSupportEmail}
            title="Support-E-Mail an Administratoren senden"
          >
            ✉️
          </button>
          <div className="settings-menu-container" style={{ position: 'relative' }}>
            <button
              className="help-button"
              onClick={(e) => {
                e.stopPropagation();
                setShowSettingsMenu(!showSettingsMenu);
              }}
              title="Einstellungen"
            >
              ⚙️
            </button>
            {showSettingsMenu && (
              <div className="user-info-kebab-menu" style={{ top: '38px', right: '0' }}>
                {availableYears.length > 1 && (
                  <div style={{ padding: '8px 12px', borderBottom: '1px solid #eee' }}>
                    <label htmlFor="year-selector-menu" style={{ fontSize: '13px', fontWeight: '500', display: 'block', marginBottom: '6px' }}>
                      Jahr:
                    </label>
                    <select
                      id="year-selector-menu"
                      value={year || ''}
                      onChange={async (e) => {
                        const newYear = parseInt(e.target.value, 10);
                        if (newYear !== year) {
                          try {
                            await changeYear(newYear);
                            // Refresh Fachbereiche after year change
                            await refreshFachbereiche();
                            setShowSettingsMenu(false);
                          } catch (error: any) {
                            alert('Fehler beim Ändern des Jahres: ' + (error.message || 'Unbekannter Fehler'));
                          }
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '6px 8px',
                        fontSize: '13px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      {availableYears.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <button
                  className="user-info-kebab-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowChangePassword(true);
                    setShowSettingsMenu(false);
                  }}
                >
                  🔒 Passwort ändern
                </button>
              </div>
            )}
          </div>
          <button 
            className="help-button"
            onClick={handleLogout}
            title="Abmelden"
          >
            ⏻
          </button>
        </div>
        <div className="user-info" style={{ display: 'flex', alignItems: 'center', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <span className="user-name">Willkommen {user.username}</span>
          {year && (
            <span style={{ fontSize: '14px', color: '#666' }}>
              Jahr: {year}
              {user.role && (
                <span style={{ marginLeft: '8px' }}>• {user.role}</span>
              )}
            </span>
          )}
        </div>
        <img src="/roemerman-logo.png" alt="RömerMan Logo" className="logo" />
        <h1 className="title">Helfermanagement</h1>
        <h2 className="subtitle">Römerman {year || 2026}</h2>
        {yearMaintenanceDisabled && (
          <div style={{
            backgroundColor: '#fff3cd',
            border: '2px solid #ffc107',
            borderRadius: '4px',
            color: '#856404',
            padding: '12px 16px',
            margin: '16px auto',
            maxWidth: '800px',
            fontSize: '0.95rem',
            fontWeight: '600',
            textAlign: 'center'
          }}>
            ⚠️ Das Jahr {year} ist schreibgeschützt. Daten können nicht erstellt, bearbeitet oder gelöscht werden. Sie können Daten anzeigen und exportieren.
          </div>
        )}
        <div className="action-buttons">
          {(canUseGoogleFormOrSync() || (yearRegistrationConfig.googleFormUrl || yearRegistrationConfig.googleSheetsConfigured)) && (
            <div className="verwaltung-menu-container" ref={helferRegistrierungMenuRef}>
              <button
                className="helper-list-button"
                onClick={() => setShowHelferRegistrierungDropdown(!showHelferRegistrierungDropdown)}
                disabled={yearMaintenanceDisabled}
                title={yearMaintenanceDisabled ? `Das Jahr ${year} ist schreibgeschützt` : "Helfer Registrierung"}
                style={{ opacity: yearMaintenanceDisabled ? 0.6 : 1, cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer' }}
              >
                Helfer Registrierung {showHelferRegistrierungDropdown ? '▲' : '▼'}
              </button>
              {showHelferRegistrierungDropdown && (
                <div className="verwaltung-menu">
                  {(isAdmin() || (user?.role === 'Fachbereich Administrator' && !yearRegistrationConfig.googleSheetsConfigured && !yearRegistrationConfig.googleFormUrl)) && (
                    <button
                      className="verwaltung-menu-item"
                      onClick={() => {
                        setShowHelferRegistrierungDropdown(false);
                        setRegistrationPopupMode('form');
                        setShowRegistrationPopup(true);
                      }}
                      title="Neuen Helfer im Helfermanagement registrieren"
                    >
                      Helfer registrieren (Helfermanagement)
                    </button>
                  )}
                  {yearRegistrationConfig.googleFormUrl && (
                    <button
                      className="verwaltung-menu-item"
                      onClick={() => {
                        setShowHelferRegistrierungDropdown(false);
                        window.open(yearRegistrationConfig.googleFormUrl!, '_blank', 'noopener,noreferrer');
                      }}
                      title="Google Formular für Helfer-Registrierung öffnen"
                    >
                      Helfer registrieren (Google Form)
                    </button>
                  )}
                  {yearRegistrationConfig.googleSheetsConfigured && (
                    <button
                      className="verwaltung-menu-item"
                      onClick={() => {
                        setShowHelferRegistrierungDropdown(false);
                        setShowGoogleSheetsSync(true);
                      }}
                      title={googleSheetsCandidateCount > 0 ? `${googleSheetsCandidateCount} neue Kandidaten in Google Sheets – Sync lohnt sich` : 'Helfer aus Google Sheets importieren'}
                    >
                      <span className="verwaltung-menu-item-row">
                        <span>Import (Google)</span>
                        {googleSheetsCandidateCount > 0 && (
                          <span className="verwaltung-menu-item-new-badge">{googleSheetsCandidateCount} neu</span>
                        )}
                      </span>
                    </button>
                  )}
                  {isAdmin() && (
                    <button
                      className="verwaltung-menu-item hide-import-on-mobile-phone"
                      onClick={() => {
                        setShowHelferRegistrierungDropdown(false);
                        setRegistrationPopupMode('excel-import');
                        setShowRegistrationPopup(true);
                      }}
                      title="Helfer aus Excel-Datei importieren"
                    >
                      Import (Excel)
                    </button>
                  )}
                  {canRegisterHelpers() && (
                    <button
                      className="verwaltung-menu-item"
                      onClick={() => {
                        setShowHelferRegistrierungDropdown(false);
                        setPreferHelperListForEdit(true);
                        // For Fachbereich Admins: filter by first assigned Fachbereich; Admins: unfiltered
                        const firstFb = !isAdmin() && user?.fachbereiche?.length ? user.fachbereiche[0] : null;
                        setSelectedFachbereich(firstFb);
                        setShowHelperList(true);
                      }}
                      title="Helferliste öffnen zum Bearbeiten (Fachbereich Admins nur ihre Fachbereiche)"
                    >
                      Helfer bearbeiten
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          {!isAdmin() && (
            <>
              {hasSingleFachbereich ? (
                <>
                  <button
                    className="helper-list-button"
                    onClick={() => setSelectedFachbereich(singleFachbereich!)}
                    title={`${singleFachbereich} Positionen`}
                  >
                    📋 {singleFachbereich} Positionen
                  </button>
                  <div className="helper-list-button-with-badge">
                    <button
                      className="helper-list-button"
                      onClick={() => {
                        setSelectedFachbereich(singleFachbereich!);
                        setShowHelperList(true);
                      }}
                      title={`${singleFachbereich} Helfer${(newHelperCounts[singleFachbereich!] && newHelperCounts[singleFachbereich!] > 0) ? ` – ${newHelperCounts[singleFachbereich!]} neue Helfer ohne Rückmeldung/Position` : ''}`}
                    >
                      👥 {singleFachbereich} Helfer
                    </button>
                    {(newHelperCounts[singleFachbereich!] && newHelperCounts[singleFachbereich!] > 0) && (
                      <span className="new-helper-badge" title="Neue Helfer ohne Rückmeldung und ohne Position">
                        {newHelperCounts[singleFachbereich!]}
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="verwaltung-menu-container" ref={fachbereichManagerMenuRef}>
                    <button 
                      className="helper-list-button"
                      onClick={() => setShowFachbereichManagerMenu(!showFachbereichManagerMenu)}
                      title="Fachbereich Positionen"
                      disabled={!canViewAllFachbereiche() && (!user?.fachbereiche || user.fachbereiche.length === 0)}
                    >
                      📋 Fachbereich Positionen {showFachbereichManagerMenu ? '▲' : '▼'}
                    </button>
                    {showFachbereichManagerMenu && (
                      <div className="verwaltung-menu">
                        {availableFachbereiche.map((fb) => (
                          <button
                            key={fb}
                            className="verwaltung-menu-item"
                            onClick={() => {
                              setSelectedFachbereich(fb);
                              setShowFachbereichManagerMenu(false);
                            }}
                            title={`Fachbereich ${fb} verwalten`}
                          >
                            {fb}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {!isViewer() && (
                  <div className={`verwaltung-menu-container ${showFachbereichHelferMenu ? 'verwaltung-menu-container-full' : ''}`} ref={fachbereichHelferMenuRef}>
                    <div className="helper-list-button-with-badge">
                      <button 
                        className="helper-list-button"
                        onClick={() => setShowFachbereichHelferMenu(!showFachbereichHelferMenu)}
                        title={(() => {
                          const fbsWithNew = availableFachbereiche.filter(fb => (newHelperCounts[fb] ?? 0) > 0).length;
                          return newHelperUniqueTotal > 0
                            ? `Fachbereich Helfer – ${newHelperUniqueTotal} neue Helfer in ${fbsWithNew} Fachbereich${fbsWithNew === 1 ? '' : 'en'}`
                            : 'Fachbereich Helfer';
                        })()}
                        disabled={!canViewAllFachbereiche() && (!user?.fachbereiche || user.fachbereiche.length === 0)}
                      >
                        👥 Fachbereich Helfer {showFachbereichHelferMenu ? '▲' : '▼'}
                      </button>
                      {newHelperUniqueTotal > 0 ? (
                        <span
                          className="new-helper-badge"
                          title={`${newHelperUniqueTotal} neue Helfer in ${availableFachbereiche.filter(fb => (newHelperCounts[fb] ?? 0) > 0).length} Fachbereich${availableFachbereiche.filter(fb => (newHelperCounts[fb] ?? 0) > 0).length === 1 ? '' : 'en'}`}
                        >
                          {newHelperUniqueTotal}
                        </span>
                      ) : null}
                    </div>
                    {showFachbereichHelferMenu && (
                      <div className="verwaltung-menu verwaltung-menu-full">
                        {newHelperUniqueTotal > 0 ? (
                          <div className="verwaltung-menu-summary">
                            {newHelperUniqueTotal} neue Helfer in {availableFachbereiche.filter(fb => (newHelperCounts[fb] ?? 0) > 0).length} Fachbereich{availableFachbereiche.filter(fb => (newHelperCounts[fb] ?? 0) > 0).length === 1 ? '' : 'en'}
                          </div>
                        ) : null}
                        {availableFachbereiche.map((fb) => (
                          <button
                            key={fb}
                            className="verwaltung-menu-item"
                            onClick={() => {
                              setSelectedFachbereich(fb);
                              setShowHelperList(true);
                              setShowFachbereichHelferMenu(false);
                            }}
                            title={`Fachbereich ${fb} Helfer anzeigen`}
                          >
                            <span className="verwaltung-menu-item-row">
                              <span>👥 {fb}</span>
                              {(newHelperCounts[fb] && newHelperCounts[fb] > 0) && (
                                <span className="verwaltung-menu-item-new-badge">{newHelperCounts[fb]} neu</span>
                              )}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  )}
                </>
              )}
            </>
          )}
          <button 
            className="helper-list-button"
            onClick={() => setShowStatistics(true)}
            title="Gesamtstatistiken anzeigen"
            disabled={!isAdmin() && !isViewer() && (!user?.fachbereiche || user.fachbereiche.length === 0)}
            style={{ marginTop: '12px' }}
          >
            📊 Gesamtstatistiken
          </button>
          {isAdmin() && (
            <div className="helper-list-button-with-badge">
              <button 
                className="helper-list-button"
                onClick={() => setShowHelperList(true)}
                title={(() => {
                  const { noFachbereich, passtNicht } = gesamtHelperStatus;
                  const total = noFachbereich + passtNicht;
                  return total > 0
                    ? `Gesamthelferliste – ${noFachbereich} ohne Fachbereich, ${passtNicht} mit Passt nicht`
                    : 'Liste der registrierten Helfer anzeigen';
                })()}
              >
                ℹ️ Gesamthelferliste
              </button>
              {(gesamtHelperStatus.noFachbereich + gesamtHelperStatus.passtNicht) > 0 && (
                <span
                  className="new-helper-badge"
                  title={`${gesamtHelperStatus.noFachbereich} ohne Fachbereich · ${gesamtHelperStatus.passtNicht} Passt nicht`}
                >
                  {gesamtHelperStatus.noFachbereich > 0 && gesamtHelperStatus.noFachbereich}
                  {gesamtHelperStatus.noFachbereich > 0 && gesamtHelperStatus.passtNicht > 0 && ' · '}
                  {gesamtHelperStatus.passtNicht > 0 && gesamtHelperStatus.passtNicht}
                </span>
              )}
            </div>
          )}
          {isAdmin() && (
            <>
              <div className="verwaltung-menu-container" ref={exportMenuRef} style={{ marginTop: '16px' }}>
                <button 
                  className="helper-list-button"
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  title="Export"
                >
                  📥 Export {showExportMenu ? '▲' : '▼'}
                </button>
                {showExportMenu && (
                  <div className="verwaltung-menu">
                    <button
                      className="verwaltung-menu-item"
                      onClick={() => {
                        exportAllHelferpunkte();
                        setShowExportMenu(false);
                      }}
                      title="Alle Helferpunkte aller Fachbereiche als Excel exportieren"
                    >
                      📋 Helferpunkte exportieren
                    </button>
                    <button
                      className="verwaltung-menu-item"
                      onClick={async () => {
                        await exportGesamthelferliste();
                        setShowExportMenu(false);
                      }}
                      title="Gesamthelferliste als Excel exportieren (wie in der Helferliste angezeigt)"
                    >
                      👥 Gesamthelferliste exportieren
                    </button>
                    <button
                      className="verwaltung-menu-item"
                      onClick={async () => {
                        await exportHelpersDirect();
                        setShowExportMenu(false);
                      }}
                      title="Helfer und Positionszuordnungen exportieren (minimale Helferinfo + detaillierte Positionszuordnungen)"
                    >
                      📋 Helfer & Positionen exportieren
                    </button>
                    <button
                      className="verwaltung-menu-item"
                      onClick={async () => {
                        await exportStatistics();
                        setShowExportMenu(false);
                      }}
                      title="Statistiken als Excel exportieren"
                    >
                      📊 Statistiken exportieren
                    </button>
                    <div className="verwaltung-menu-divider"></div>
                    <button
                      className="verwaltung-menu-item"
                      onClick={() => {
                        exportBackup();
                        setShowExportMenu(false);
                      }}
                      title="Vollständiges System-Backup erstellen (alle Daten)"
                    >
                      💾 Backup erstellen
                    </button>
                    <button
                      className="verwaltung-menu-item"
                      onClick={openLabelsExportPopup}
                      title="PDF-Export der Lables für Helfertüten"
                    >
                      🖨️ Labels für Helfertüten
                    </button>
                  </div>
                )}
              </div>
              <div className="verwaltung-menu-container" ref={verwaltungMenuRef}>
              <button 
                className="helper-list-button"
                  onClick={() => setShowVerwaltungMenu(!showVerwaltungMenu)}
                  title="Verwaltung"
                >
                  ⚙️ Verwaltung {showVerwaltungMenu ? '▲' : '▼'}
                </button>
                {showVerwaltungMenu && (
                  <div className="verwaltung-menu">
                    <button
                      className="verwaltung-menu-item"
                      onClick={() => {
                        setShowUserManagement(true);
                        setShowVerwaltungMenu(false);
                      }}
                title="Benutzerverwaltung"
              >
                👥 Benutzerverwaltung
              </button>
                    <button
                      className="verwaltung-menu-item"
                      onClick={async () => {
                        setShowFachbereicheManagement(true);
                        setShowVerwaltungMenu(false);
                        await refreshFachbereiche();
                      }}
                      title="Fachbereiche verwalten"
                    >
                      🏢 Fachbereiche verwalten
                    </button>
                    <button
                      className="verwaltung-menu-item"
                      onClick={() => {
                        setShowYearManagement(true);
                        setShowVerwaltungMenu(false);
                      }}
                      title="Jahre verwalten"
                    >
                      📅 Jahre verwalten
                    </button>
                    <div className="verwaltung-menu-divider"></div>
                    <button
                      className="verwaltung-menu-item"
                      onClick={() => {
                        if (!yearMaintenanceDisabled) {
                          setShowRestoreDialog(true);
                          setShowVerwaltungMenu(false);
                        }
                      }}
                      disabled={yearMaintenanceDisabled}
                      title={yearMaintenanceDisabled ? `Das Jahr ${year} ist schreibgeschützt - Backup kann nicht wiederhergestellt werden` : "Backup wiederherstellen"}
                      style={{ opacity: yearMaintenanceDisabled ? 0.6 : 1, cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer' }}
                    >
                      🔄 Backup wiederherstellen
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
          {isAdmin() && (
            <div className={`verwaltung-menu-container ${showFachbereichMenu ? 'verwaltung-menu-container-full' : ''}`} ref={fachbereichMenuRef} style={{ marginTop: '12px' }}>
              <button 
                className="helper-list-button"
                onClick={() => setShowFachbereichMenu(!showFachbereichMenu)}
                title="Fachbereich Positionen"
              >
                📋 Fachbereich Positionen {showFachbereichMenu ? '▲' : '▼'}
              </button>
              {showFachbereichMenu && (
                <div className="verwaltung-menu verwaltung-menu-full">
                  {fachbereiche.map((fb) => (
                    <button
                      key={fb}
                      className="verwaltung-menu-item"
                      onClick={() => {
                        setSelectedFachbereich(fb);
                        setShowFachbereichMenu(false);
                      }}
                      title={`Fachbereich ${fb} verwalten`}
                    >
                    {fb}
                    </button>
                ))}
                </div>
              )}
            </div>
          )}
          {isAdmin() && (
            <div className={`verwaltung-menu-container ${showFachbereichHelferMenu ? 'verwaltung-menu-container-full' : ''}`} ref={fachbereichHelferMenuRef} style={{ marginTop: '4px' }}>
              <div>
                <button 
                  className="helper-list-button"
                  onClick={() => setShowFachbereichHelferMenu(!showFachbereichHelferMenu)}
                  title="Fachbereich Helfer"
                >
                  👥 Fachbereich Helfer {showFachbereichHelferMenu ? '▲' : '▼'}
                </button>
              </div>
              {showFachbereichHelferMenu && (
                <div className="verwaltung-menu verwaltung-menu-full">
                  {newHelperUniqueTotal > 0 ? (
                    <div className="verwaltung-menu-summary">
                      {newHelperUniqueTotal} neue Helfer in {fachbereiche.filter(fb => (newHelperCounts[fb] ?? 0) > 0).length} Fachbereich{fachbereiche.filter(fb => (newHelperCounts[fb] ?? 0) > 0).length === 1 ? '' : 'en'}
                    </div>
                  ) : null}
                  {fachbereiche.map((fb) => (
                      <button
                      key={fb}
                      className="verwaltung-menu-item"
                      onClick={() => {
                        setSelectedFachbereich(fb);
                        setShowHelperList(true);
                        setShowFachbereichHelferMenu(false);
                        // Fetch helpers for FachbereichHelperList
                        if (token) {
                          setLoadingFachbereichHelpers(true);
                          axios.get(`${API_BASE_URL}/api/fachbereich/${encodeURIComponent(fb)}`, {
                            headers: {
                              'Authorization': `Bearer ${token}`
                            }
                          })
                            .then(response => {
                              if (response.data.success) {
                                setFachbereichHelpers(response.data.helpers || []);
                              }
                            })
                            .catch(err => {
                              console.error('Error fetching helpers:', err);
                            })
                            .finally(() => {
                              setLoadingFachbereichHelpers(false);
                            });
                          }
                        }}
                        title={`Fachbereich ${fb} Helfer anzeigen`}
                      >
                        <span className="verwaltung-menu-item-row">
                          <span>👥 {fb}</span>
                          {(newHelperCounts[fb] && newHelperCounts[fb] > 0) && (
                            <span className="verwaltung-menu-item-new-badge">{newHelperCounts[fb]} neu</span>
                          )}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
        </div>
      </div>
      {showRegistrationPopup && (
        <RegistrationPopup 
          openMode={registrationPopupMode}
          onClose={() => setShowRegistrationPopup(false)}
          onSuccess={handleRegistrationSuccess}
          onNavigateToHelperList={() => setShowHelperList(true)}
          yearMaintenanceDisabled={yearMaintenanceDisabled}
        />
      )}
      {showGoogleSheetsSync && (
        <GoogleSheetsSync
          onClose={() => setShowGoogleSheetsSync(false)}
          onSuccess={fetchGoogleSheetsCandidateCount}
        />
      )}
      {showHelperList && (
        <>
          {!isAdmin() && selectedFachbereich && (canViewAllFachbereiche() || (user?.fachbereiche && user.fachbereiche.includes(selectedFachbereich))) && !preferHelperListForEdit ? (
            loadingFachbereichHelpers ? (
              <div className="helper-list-overlay" onClick={() => setShowHelperList(false)}>
                <div className="helper-list-container" onClick={(e) => e.stopPropagation()}>
                  <div style={{ padding: '40px', textAlign: 'center' }}>Lädt...</div>
                </div>
              </div>
            ) : (
              <FachbereichHelperList
                fachbereich={selectedFachbereich}
                helpers={fachbereichHelpers}
                defaultToNewHelperFilters={(newHelperCounts[selectedFachbereich] ?? 0) > 0}
                onClose={() => {
                  setShowHelperList(false);
                  setSelectedFachbereich(null);
                  setFachbereichHelpers([]);
                  setPreferHelperListForEdit(false);
                }}
                onUpdate={async () => {
                  // Refresh helpers when updated
                  if (selectedFachbereich && token) {
                    try {
                      const response = await axios.get(`${API_BASE_URL}/api/fachbereich/${encodeURIComponent(selectedFachbereich)}`, {
                        headers: {
                          'Authorization': `Bearer ${token}`
                        }
                      });
                      if (response.data.success) {
                        setFachbereichHelpers(response.data.helpers || []);
                      }
                    } catch (err) {
                      console.error('Error fetching helpers:', err);
                    }
                  }
                }}
                onEditFachbereich={(fachbereich) => {
                  setShowHelperList(false);
                  setSelectedFachbereich(fachbereich);
                }}
                yearMaintenanceDisabled={yearMaintenanceDisabled || isViewer()}
                readOnly={isViewer()}
              />
            )
          ) : (
            <HelperList 
              onClose={() => {
                setShowHelperList(false);
                setHelperToEdit(null);
                setSelectedFachbereich(null);
                setPreferHelperListForEdit(false);
                setInitialShowKuchenspenderForHelperList(false);
              }} 
              initialFachbereich={selectedFachbereich || undefined}
              initialShowKuchenspender={initialShowKuchenspenderForHelperList}
              initialHelperToEdit={helperToEdit}
              adminStatusCounts={isAdmin() ? gesamtHelperStatus : undefined}
              onRefreshStatus={isAdmin() ? fetchGesamtHelperStatus : undefined}
              yearMaintenanceDisabled={yearMaintenanceDisabled}
              onEditFachbereich={(fachbereich) => {
                // Open helper list instead of positions view
                // If user has edit rights for this Fachbereich → open FachbereichHelperList
                // Otherwise → open HelperList with filter
                const isMyFachbereich = user?.fachbereiche && user.fachbereiche.includes(fachbereich);
                const hasEditRights = isAdmin() || isMyFachbereich;
                
                if (hasEditRights && !isAdmin()) {
                  // User has edit rights (Fachbereich Administrator) → open FachbereichHelperList
                  setPreferHelperListForEdit(false); // Allow switch to FachbereichHelperList for position assignment
                  setSelectedFachbereich(fachbereich);
                  setShowHelperList(true);
                  // Fetch helpers for FachbereichHelperList
                  if (token) {
                    setLoadingFachbereichHelpers(true);
                    axios.get(`${API_BASE_URL}/api/fachbereich/${encodeURIComponent(fachbereich)}`, {
                      headers: {
                        'Authorization': `Bearer ${token}`
                      }
                    })
                    .then(response => {
                      if (response.data.success) {
                        setFachbereichHelpers(response.data.helpers || []);
                      }
                    })
                    .catch(err => {
                      console.error('Error fetching helpers:', err);
                    })
                    .finally(() => {
                      setLoadingFachbereichHelpers(false);
                    });
                  }
                } else {
                  // Admin or no edit rights → open HelperList with filter
                  setSelectedFachbereich(fachbereich);
                  setShowHelperList(true);
                }
              }}
            />
          )}
        </>
      )}
      {selectedFachbereich && !showHelperList && (
        <FachbereichManagement 
          fachbereich={selectedFachbereich}
          onClose={() => setSelectedFachbereich(null)}
          defaultToNewHelperFilters={(newHelperCounts[selectedFachbereich] ?? 0) > 0}
          readOnly={isViewer()}
        />
      )}
      {showHelp && (
        <Help onClose={() => setShowHelp(false)} />
      )}
      {showSupportEmailModal && (
        <SupportEmailModal onClose={() => setShowSupportEmailModal(false)} />
      )}
      {showUserManagement && (
        <UserManagement onClose={() => setShowUserManagement(false)} />
      )}
      {showChangePassword && (
        <ChangePassword onClose={() => setShowChangePassword(false)} />
      )}
      {showStatistics && token && (
        <Statistics 
          onClose={() => setShowStatistics(false)} 
          token={token}
          onNavigateToHelperList={(fachbereich?: string, filterKuchenspender?: boolean) => {
            // If clicking on Kuchenspenden → open HelperList filtered by Kuchenspender
            if (filterKuchenspender) {
              setSelectedFachbereich(null);
              setPreferHelperListForEdit(true);
              setInitialShowKuchenspenderForHelperList(true);
              setShowHelperList(true);
              return;
            }
            // If clicking on user's own Fachbereich → open FachbereichHelperList (helper list)
            // If clicking on other Fachbereich or Beobachter → open HelperList (Registrierte Helfer) with Fachbereich filter
            // Beobachter (Viewer) has no Fachbereich Helfer button → open HelperList filtered by Fachbereich
            if (fachbereich) {
              const isMyFachbereich = user?.fachbereiche && user.fachbereiche.includes(fachbereich);
              const useFachbereichHelperList = (isMyFachbereich && !isAdmin()) && !isViewer();
              if (useFachbereichHelperList) {
                // Open FachbereichHelperList (helper list) for user's Fachbereich
                setSelectedFachbereich(fachbereich);
                setPreferHelperListForEdit(false);
                setShowHelperList(true);
                if (token) {
                  setLoadingFachbereichHelpers(true);
                  axios.get(`${API_BASE_URL}/api/fachbereich/${encodeURIComponent(fachbereich)}`, {
                    headers: {
                      'Authorization': `Bearer ${token}`
                    }
                  })
                  .then(response => {
                    if (response.data.success) {
                      setFachbereichHelpers(response.data.helpers || []);
                    }
                  })
                  .catch(err => {
                    console.error('Error fetching helpers:', err);
                  })
                  .finally(() => {
                    setLoadingFachbereichHelpers(false);
                  });
                }
              } else {
                // Admin, Beobachter, or no edit rights → open HelperList (Registrierte Helfer) with Fachbereich filter
                setSelectedFachbereich(fachbereich);
                setPreferHelperListForEdit(true);
                setShowHelperList(true);
              }
            } else {
              setSelectedFachbereich(null);
              setPreferHelperListForEdit(false);
              setShowHelperList(true);
            }
          }}
          onNavigateToFachbereichPositions={(fachbereich: string) => {
            // Open FachbereichManagement (positions view) for the selected Fachbereich
            setSelectedFachbereich(fachbereich);
            setShowHelperList(false);
          }}
        />
      )}
      {showFachbereicheManagement && isAdmin() && token && (
        <FachbereicheManagement 
          onClose={async () => {
            setShowFachbereicheManagement(false);
            // Refresh Fachbereiche after management closes
            await refreshFachbereiche();
          }} 
          token={token} 
        />
      )}
      {showYearManagement && isAdmin() && token && (
        <YearManagement 
          onClose={async () => {
            setShowYearManagement(false);
            // Refresh available years after management closes
            await refreshUser();
            await refreshFachbereiche();
          }} 
          token={token} 
        />
      )}

      {/* Restore Backup Dialog */}
      {showRestoreDialog && isAdmin() && (
        <div 
          className="cleanup-confirm-overlay" 
          style={{ zIndex: 10000 }}
          onClick={() => {
            if (!restoring) {
              setShowRestoreDialog(false);
              setRestoreFile(null);
              if (restoreFileInputRef.current) {
                restoreFileInputRef.current.value = '';
              }
            }
          }}
        >
          <div 
            className="cleanup-confirm-dialog cleanup-confirm-content" 
            style={{ maxWidth: '500px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Backup wiederherstellen</h3>
            {yearMaintenanceDisabled && (
              <div style={{ backgroundColor: '#fff3cd', border: '2px solid #ffc107', borderRadius: '4px', color: '#856404', marginBottom: '20px', padding: '12px', fontSize: '0.95rem', fontWeight: '600' }}>
                ⚠️ Das Jahr {year} ist schreibgeschützt. Backup kann nicht wiederhergestellt werden.
              </div>
            )}
            <p style={{ marginBottom: '20px' }}>
              Wählen Sie eine Backup-Datei (.xlsx) aus, um das System wiederherzustellen.
            </p>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                <input
                  type="checkbox"
                  checked={overwriteMode}
                  onChange={(e) => setOverwriteMode(e.target.checked)}
                  disabled={yearMaintenanceDisabled}
                  style={{ width: '20px', height: '20px', cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer', opacity: yearMaintenanceDisabled ? 0.6 : 1 }}
                />
                <span style={{ fontWeight: 'bold', color: overwriteMode ? '#d32f2f' : '#333', opacity: yearMaintenanceDisabled ? 0.6 : 1 }}>
                  Überschreiben (Alle bestehenden Daten löschen und ersetzen)
                </span>
              </label>
              {overwriteMode && (
                <p style={{ marginLeft: '30px', color: '#d32f2f', fontWeight: 'bold', fontSize: '0.9rem' }}>
                  ⚠️ WARNUNG: Im Überschreiben-Modus werden ALLE bestehenden Daten gelöscht und durch die Backup-Daten ersetzt!
                </p>
              )}
              {!overwriteMode && (
                <p style={{ marginLeft: '30px', color: '#666', fontSize: '0.9rem' }}>
                  Bestehende Daten werden aktualisiert oder neue Daten hinzugefügt. Bestehende Datensätze werden anhand ihrer eindeutigen Identifikation (z.B. E-Mail für Helfer) aktualisiert.
                </p>
              )}
            </div>
            <div style={{ marginBottom: '20px' }}>
              <input
                ref={restoreFileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleRestoreFileSelect}
                disabled={yearMaintenanceDisabled}
                style={{
                  padding: '8px',
                  fontSize: '14px',
                  width: '100%',
                  boxSizing: 'border-box',
                  opacity: yearMaintenanceDisabled ? 0.6 : 1,
                  cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer'
                }}
              />
              {restoreFile && (
                <p style={{ marginTop: '10px', color: '#666' }}>
                  Ausgewählte Datei: {restoreFile.name}
                </p>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                className="helper-list-button"
                onClick={() => {
                  setShowRestoreDialog(false);
                  setRestoreFile(null);
                  if (restoreFileInputRef.current) {
                    restoreFileInputRef.current.value = '';
                  }
                }}
                disabled={restoring}
              >
                Abbrechen
              </button>
              <button
                className="helper-list-button"
                onClick={handleRestore}
                disabled={!restoreFile || restoring || yearMaintenanceDisabled}
                title={yearMaintenanceDisabled ? `Das Jahr ${year} ist schreibgeschützt` : ''}
                style={{
                  backgroundColor: restoring || yearMaintenanceDisabled ? '#ccc' : '#4caf50',
                  color: 'white',
                  opacity: yearMaintenanceDisabled ? 0.6 : 1,
                  cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer'
                }}
              >
                {restoring ? 'Wiederherstellen...' : 'Wiederherstellen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Progress Dialog */}
      {restoring && restoreProgress && (
        <div className="restore-progress-overlay">
          <div className="restore-progress-dialog">
            <h3>Backup wird wiederhergestellt...</h3>
            <div className="restore-progress-content">
              <div className="restore-progress-step">
                {restoreProgress.stepName}
              </div>
              {restoreProgress.currentArea && restoreProgress.totalAreas && (
                <div className="restore-progress-area">
                  Bereich: {restoreProgress.currentArea} ({restoreProgress.step} von {restoreProgress.totalAreas})
                </div>
              )}
              <div className="restore-progress-bar">
                <div 
                  className="restore-progress-bar-fill"
                  style={{ width: `${(restoreProgress.step / (restoreProgress.totalAreas || 7)) * 100}%` }}
                />
              </div>
              <div className="restore-progress-text">
                Schritt {restoreProgress.step} von {restoreProgress.totalAreas || 7}
              </div>
            </div>
          </div>
        </div>
      )}

      {showLabelsExportPopup && (
        <LabelsExportPopup
          onClose={() => {
            setShowLabelsExportPopup(false)
          }}/>
      )}
    </div>
  );
};

const App: React.FC = () => {
  return <AppContent />;
};

export default App;

